import React, { useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Loader2, X, Tag } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Inline "Report wrong price" button + modal.
 *
 * Renders next to the price on a campsite detail page. When a camper
 * spots a price they know is wrong, one tap → enter the correct price →
 * submit. The report goes to the admin queue (/admin → Price Reports).
 *
 * Anonymous users see a soft prompt to log in (we keep this gated so we
 * can prevent abuse and let users override their own report later).
 */
export default function ReportPriceButton({ campsiteId, currentPrice = null, variant = "default" }) {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  // Variant copy: 'help'    = social-proof-flow CTA shown under PriceVerifiedPill;
  //               'pill'    = compact browse-card pill next to the price;
  //               'default' = original "Wrong price?" inline link.
  const _isHelp = variant === "help";
  const _isPill = variant === "pill";
  const _label = _isPill
    ? "Share your price"
    : _isHelp
      ? "Got an updated price? Help fellow campers"
      : "Wrong price?";
  const _triggerClass = _isPill
    ? "inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-orange-50 text-[#ff4500] border border-orange-200 hover:bg-orange-100 mono"
    : _isHelp
      ? "inline-flex items-center gap-1 mt-1 text-[10px] sm:text-[11px] mono text-[#ff4500] hover:underline underline-offset-2 font-medium"
      : "inline-flex items-center gap-1 text-[11px] mono text-[#888] hover:text-[#ff4500] underline-offset-2 hover:underline";

  const submit = async (e) => {
    e.preventDefault();
    const p = parseFloat(price);
    if (Number.isNaN(p) || p < 0 || p > 500) {
      toast.error("Enter a price between £0 and £500");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/campsites/${campsiteId}/report-price`, {
        campsite_id: campsiteId,
        correct_price: p,
        note: note.trim() || null,
      });
      toast.success("Thanks! We'll review your report and update the price soon.");
      setOpen(false);
      setPrice("");
      setNote("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    // Anon users: render a discreet prompt — clicking it nudges to login.
    return (
      <button
        onClick={() => toast("Log in to share the price you paid — helps every other camper.", { icon: "🔐" })}
        className={_triggerClass}
        data-testid="report-price-cta-anon"
      >
        {_isPill ? <Tag className="w-2.5 h-2.5" /> : <AlertCircle className="w-3 h-3" />} {_label}{_isPill ? "" : " →"}
      </button>
    );
  }

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={_triggerClass}
        data-testid="report-price-btn"
      >
        {_isPill ? <Tag className="w-2.5 h-2.5" /> : <AlertCircle className="w-3 h-3" />} {_label}{_isPill ? "" : " →"}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="report-price-modal"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-white max-w-md w-full p-6 border-2 border-[#ff4500] shadow-[0_8px_0_0_#111]"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="eyebrow text-[#ff4500]">{_isPill || _isHelp ? "Help fellow campers" : "Help us stay honest"}</div>
                <h4 className="font-display text-2xl mt-1">{_isPill || _isHelp ? "Share the price you paid" : "Report a wrong price"}</h4>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 hover:bg-[#fafafa]"
                aria-label="Close"
                data-testid="report-price-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-[#555] leading-relaxed mb-4">
              {_isPill || _isHelp
                ? "Stayed here recently? Drop the per-night price you actually paid. Multiple reports get an instant \"verified by campers\" badge on the listing."
                : "Spotted a price that doesn't match Pitchup or the campsite directly? Drop the right one below and we'll verify + update it. Multiple reports get priority."
              }
            </p>

            <label className="block mb-3">
              <span className="eyebrow block mb-1.5">Correct price · £/night</span>
              <input
                type="number"
                min="0"
                max="500"
                step="0.50"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={currentPrice ? `Currently £${currentPrice.toFixed(2)}` : "e.g. 28.50"}
                required
                className="w-full border hairline px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff4500]"
                data-testid="report-price-input"
              />
            </label>

            <label className="block mb-4">
              <span className="eyebrow block mb-1.5">Note (optional)</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 300))}
                placeholder="e.g. Pitchup shows £32 for 2 adults May-Sept"
                rows={3}
                maxLength={300}
                className="w-full border hairline px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff4500] resize-none"
                data-testid="report-price-note"
              />
              <span className="text-[10px] mono text-[#888] block mt-1 text-right">{note.length} / 300</span>
            </label>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs mono uppercase tracking-wider px-4 py-2 border hairline hover:bg-[#fafafa]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="btn-primary text-xs px-5 py-2 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="report-price-submit"
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Submit report
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
